/**
 * admin_database_migrations_controller.js — Admin > Database > Migrations (Stimulus).
 *
 * Lint, validate and generate-from-alignment buttons, each showing its output
 * in the page's result modal. Ported from an inline <script> that rendered
 * twice (its block sat inside {% block content %}) and was not Turbo-safe.
 *
 * Wiring: data-controller="admin-database-migrations" on the page root, with
 * data-admin-database-migrations-schemas-url-value (the alignment page the
 * generated-migration result links to). Buttons call #lint / #validate with a
 * data-admin-database-migrations-type-param, and #generate with a
 * data-admin-database-migrations-schema-param.
 *
 * Template: templates/admin/database/migrations.html
 */

import { Controller } from "@hotwired/stimulus";

const SPINNER = '<div class="text-center"><div class="spinner-border"></div></div>';

function postJson(url, body) {
    return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
    }).then(function (response) { return response.json(); });
}

export default class extends Controller {
    static values = { schemasUrl: String };

    lint(event) {
        const type = event.params.type;
        this._showResult('Linting ' + type + ' migrations...', SPINNER);
        postJson('/admin/database/api/lint', { type: type }).then((data) => {
            if (data.success) {
                this._showResult('Lint Results: ' + type,
                    '<div class="alert alert-success"><i class="bi bi-check-circle me-2"></i>Lint completed</div>'
                    + '<pre class="bg-light p-3 rounded">' + (data.stdout || 'No issues found') + '</pre>');
            } else {
                this._showResult('Lint Error', '<div class="alert alert-danger">' + (data.stderr || data.message) + '</div>');
            }
        });
    }

    validate(event) {
        const type = event.params.type;
        this._showResult('Validating ' + type + ' migrations...', SPINNER);
        postJson('/admin/database/api/validate', { type: type }).then((data) => {
            if (data.success) {
                this._showResult('Validation Results: ' + type,
                    '<div class="alert alert-success"><i class="bi bi-check-circle me-2"></i>Validation passed</div>'
                    + '<pre class="bg-light p-3 rounded">' + (data.stdout || 'All migrations valid') + '</pre>');
            } else {
                this._showResult('Validation Error',
                    '<div class="alert alert-danger"><i class="bi bi-x-circle me-2"></i>Validation failed</div>'
                    + '<pre class="bg-light p-3 rounded">' + (data.stderr || data.message) + '</pre>');
            }
        });
    }

    generate(event) {
        const schema = event.params.schema;
        this._showResult('Generating Migration for ' + schema + '...',
            '<div class="text-center"><div class="spinner-border"></div><p class="mt-2">Analyzing model differences...</p></div>');
        postJson('/admin/database/api/generate-from-alignment', { schema: schema })
            .then((data) => {
                if (data.success) {
                    this._showResult('Migration Generated', this._generatedContent(schema, data));
                } else {
                    this._showResult('Generation Error',
                        '<div class="alert alert-danger"><i class="bi bi-x-circle me-2"></i>Failed to generate migration</div>'
                        + '<p>' + (data.error || data.message) + '</p>');
                }
            })
            .catch((error) => {
                this._showResult('Error',
                    '<div class="alert alert-danger"><i class="bi bi-x-circle me-2"></i>Network error</div>'
                    + '<p>' + error.message + '</p>');
            });
    }

    _generatedContent(schema, data) {
        let content = '<div class="alert alert-success"><i class="bi bi-check-circle me-2"></i>Migration generated successfully!</div>'
            + '<p><strong>File:</strong> <code>' + data.filename + '</code></p>'
            + '<p><strong>Type:</strong> ' + (schema === 'public' ? 'public' : 'tenant') + '</p>';
        if (data.diff_sql) {
            content += '<h6 class="mt-3">Generated SQL:</h6><pre class="bg-light p-3 rounded"><code>' + data.diff_sql + '</code></pre>';
        }
        return content + '<div class="mt-3">'
            + '<a href="' + this.schemasUrlValue + '#' + schema + '" class="btn btn-primary me-2">'
            + '<i class="bi bi-eye me-1"></i>Review Alignment</a>'
            + '<button class="btn btn-secondary" data-action="reload-page">'
            + '<i class="bi bi-arrow-clockwise me-1"></i>Refresh Page</button></div>';
    }

    _showResult(title, content) {
        document.getElementById('resultModalTitle').textContent = title;
        document.getElementById('resultContent').innerHTML = content;
        bootstrap.Modal.getOrCreateInstance(document.getElementById('resultModal')).show();
    }
}
